import TrialProgress, { TrialPlansMarker } from "@/components/TrialProgress";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBillingLifecycle } from "@/lib/billingLifecycle";
import { SubscriptionAction } from "@/components/SubscriptionExpiryBanner";
import { Button } from "@/components/ui/button";

export function LockCard() {
  const { logout } = useAuth();
  const { data, refresh } = useBillingLifecycle();
  if (!data) return null;
  const state = data.lifecycle_state;
  const trial = state === "trial_expired";
  const payment = state === "payment_locked";
  const review = state === "suspended";
  const manager = data.can_manage_billing;
  const plans = manager && data.recommended_action === "choose_plan";
  const title = trial ? "Your Fieldlink trial has ended" : payment ? "Payment required" : review ? "Your subscription needs review" : "Your Fieldlink subscription has ended";
  const message = !manager ? "Your company's Fieldlink subscription needs attention. Please contact your company administrator."
    : trial ? "Choose a plan to continue using Fieldlink."
    : review ? "Contact Fieldlink support to review your company's access."
    : payment ? (plans ? "Choose a plan to restore access." : "Update your billing details to restore access.")
    : "Restart your subscription to continue using Fieldlink.";
  return <section className="px-4 py-8 lg:p-10 max-w-6xl mx-auto" data-testid="subscription-lock-page">
    <div className="border-l-4 border-[#FF5F15] pl-5">
      <p className="text-xs font-mono text-zinc-500 mb-2">{data.org_name} · {data.seat_usage} active users</p>
      <h1 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight">{title}</h1>
      <p className="mt-3 font-semibold">Your company data is safe.</p><p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{message}</p>
    </div>
    {trial && manager && <TrialProgress compact />}
    {plans && <TrialPlansMarker />}
    {plans && <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
      {data.tiers.map(tier => {
        const fits = tier.max_staff == null || data.seat_usage <= tier.max_staff;
        return <div key={tier.id} className={`hard-card p-4 flex flex-col gap-3 ${data.suggested_plan === tier.id ? "border-[#FF5F15] border-2" : ""}`}>
          <div><h2 className="font-bold text-lg">{tier.name}</h2>{data.suggested_plan === tier.id && <p className="text-xs text-orange-700 dark:text-orange-300">Fits your current team</p>}</div>
          <p className="font-bold text-xl">{tier.monthly_price == null ? "Custom" : `$${tier.monthly_price.toFixed(2)}`} {tier.monthly_price != null && <span className="text-xs font-normal">NZD / month</span>}</p>
          <p className="text-sm flex-1">{tier.blurb}</p>
          <SubscriptionAction action="choose_plan" tier={tier} disabled={!fits} label={tier.monthly_price == null ? "Contact Fieldlink" : fits ? "Choose plan" : "Your team exceeds this cap"} />
        </div>;
      })}
    </div>}
    <div className="flex flex-wrap gap-2 mt-6">
      {!plans && <SubscriptionAction action={data.recommended_action} label={data.recommended_action === "update_payment" ? "Update payment" : "Contact support"} />}
      {manager && <Button asChild variant="outline"><Link to="/app/billing">Billing</Link></Button>}
      <Button asChild variant="outline"><Link to="/app/support">Contact support</Link></Button>
      <Button variant="outline" onClick={() => refresh({ reconcile: manager })}>Check access</Button>
      <Button variant="ghost" onClick={logout}>Log out</Button>
    </div>
  </section>;
}

export default function SubscriptionLockedPage() {
  const { data } = useBillingLifecycle();
  const nav = useNavigate();
  useEffect(() => { if (data && !data.locked) nav("/app", { replace: true }); }, [data, nav]);
  return <LockCard />;
}

export function SubscriptionLockGate({ children }) {
  const { user } = useAuth();
  const { data, error, refresh } = useBillingLifecycle();
  const loc = useLocation();
  if (!user || user.role === "super_admin" || loc.pathname.startsWith("/app/support")) return children;
  if (!data) return <div className="p-8" role="status">{error ? <>Could not check subscription access. <Button variant="outline" onClick={() => refresh()}>Retry</Button></> : "Checking subscription…"}</div>;
  if (!data.locked || (data.can_manage_billing && loc.pathname.startsWith("/app/billing"))) return children;
  return <LockCard />;
}
